import React, { useState, useEffect, useCallback } from 'react'
import { Bug, Lightbulb, ExternalLink, Book, MessageCircle, Mail, Loader2, Check } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/utils/api'

interface FeedbackItem {
  id: string
  title: string
  status: string
  created_at: string
  github_url?: string
}

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  open: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Open' },
  in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'In Progress' },
  closed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Closed' },
  resolved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Resolved' },
  wont_fix: { bg: 'bg-gray-100', text: 'text-gray-600', label: "Won't Fix" },
  duplicate: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Duplicate' },
  planned: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Planned' },
  under_review: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Under Review' },
}

export default function SupportSettings() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'report' | 'request' | 'feedback'>('report')

  // Bug report form
  const [bugTitle, setBugTitle] = useState('')
  const [bugDescription, setBugDescription] = useState('')
  const [bugSteps, setBugSteps] = useState('')
  const [bugExpected, setBugExpected] = useState('')
  const [submittingBug, setSubmittingBug] = useState(false)
  const [bugSuccess, setBugSuccess] = useState(false)

  // Feature request form
  const [featureTitle, setFeatureTitle] = useState('')
  const [featureDescription, setFeatureDescription] = useState('')
  const [featureUseCase, setFeatureUseCase] = useState('')
  const [submittingFeature, setSubmittingFeature] = useState(false)
  const [featureSuccess, setFeatureSuccess] = useState(false)

  // Feedback list
  const [bugReports, setBugReports] = useState<FeedbackItem[]>([])
  const [featureRequests, setFeatureRequests] = useState<FeedbackItem[]>([])
  const [loadingFeedback, setLoadingFeedback] = useState(false)

  const familySlug = typeof window !== 'undefined' ? window.location.hostname.split('.')[0] : ''

  const loadFeedback = useCallback(async () => {
    setLoadingFeedback(true)
    try {
      const response = await apiClient.get('/api/feedback/my-submissions')
      setBugReports(response.bug_reports || [])
      setFeatureRequests(response.feature_requests || [])
    } catch (error) {
      console.error('Failed to load feedback:', error)
      setBugReports([])
      setFeatureRequests([])
    } finally {
      setLoadingFeedback(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'feedback') {
      loadFeedback()
    }
  }, [activeTab, loadFeedback])

  const submitBugReport = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!bugTitle.trim() || !bugDescription.trim()) return

    setSubmittingBug(true)
    try {
      const body = `**Bug Description**
${bugDescription}

**Steps to Reproduce**
${bugSteps || 'Not provided'}

**Expected Behavior**
${bugExpected || 'Not provided'}

**Environment**
- Browser: ${navigator.userAgent}
- User: ${user?.username}
- Family: ${familySlug}

---
*Submitted via HousePoints Web App*`

      await apiClient.post('/api/feedback/bug-report', {
        title: `[Web] ${bugTitle}`,
        body,
        labels: ['bug', 'web'],
      })

      setBugSuccess(true)
      setBugTitle('')
      setBugDescription('')
      setBugSteps('')
      setBugExpected('')
      setTimeout(() => setBugSuccess(false), 3000)
    } catch (error) {
      console.error('Failed to submit bug report:', error)
      alert('Failed to submit bug report. Please try again.')
    } finally {
      setSubmittingBug(false)
    }
  }

  const submitFeatureRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!featureTitle.trim() || !featureDescription.trim()) return

    setSubmittingFeature(true)
    try {
      const body = `**Feature Description**
${featureDescription}

**Use Case**
${featureUseCase || 'Not provided'}

**Submitted By**
- User: ${user?.username}
- Family: ${familySlug}

---
*Submitted via HousePoints Web App*`

      await apiClient.post('/api/feedback/feature-request', {
        title: `[Feature] ${featureTitle}`,
        body,
        labels: ['enhancement', 'web'],
      })

      setFeatureSuccess(true)
      setFeatureTitle('')
      setFeatureDescription('')
      setFeatureUseCase('')
      setTimeout(() => setFeatureSuccess(false), 3000)
    } catch (error) {
      console.error('Failed to submit feature request:', error)
      alert('Failed to submit feature request. Please try again.')
    } finally {
      setSubmittingFeature(false)
    }
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const getStatusStyle = (status: string) => {
    return STATUS_STYLES[status.toLowerCase()] || STATUS_STYLES.open
  }

  return (
    <div className="space-y-6">
      {/* External Links */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Links</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <a
            href="https://housepoints.ai/docs/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
          >
            <Book className="w-5 h-5 text-indigo-600" />
            <span className="text-sm font-medium text-gray-700">Documentation</span>
            <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
          </a>
          <a
            href="https://discord.gg/KuTaPcyQdk"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
          >
            <MessageCircle className="w-5 h-5 text-indigo-600" />
            <span className="text-sm font-medium text-gray-700">Discord</span>
            <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
          </a>
          <a
            href="mailto:support@junoax.com"
            className="flex items-center gap-2 px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
          >
            <Mail className="w-5 h-5 text-indigo-600" />
            <span className="text-sm font-medium text-gray-700">Email Support</span>
            <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
          </a>
          <a
            href={`https://${familySlug}.housepoints.ai/api/docs`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
          >
            <Book className="w-5 h-5 text-indigo-600" />
            <span className="text-sm font-medium text-gray-700">API Docs</span>
            <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('report')}
            className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'report'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Bug className="w-4 h-4 inline mr-2" />
            Report Bug
          </button>
          <button
            onClick={() => setActiveTab('request')}
            className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'request'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Lightbulb className="w-4 h-4 inline mr-2" />
            Request Feature
          </button>
          <button
            onClick={() => setActiveTab('feedback')}
            className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'feedback'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Feedback
          </button>
        </nav>
      </div>

      {/* Bug Report Form */}
      {activeTab === 'report' && (
        <form onSubmit={submitBugReport} className="space-y-4">
          {bugSuccess && (
            <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
              <Check className="w-5 h-5" />
              Bug report submitted successfully. Thank you for your feedback!
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={bugTitle}
              onChange={(e) => setBugTitle(e.target.value)}
              placeholder="Brief description of the issue"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              What happened? *
            </label>
            <textarea
              value={bugDescription}
              onChange={(e) => setBugDescription(e.target.value)}
              placeholder="Describe the bug you encountered..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Steps to reproduce
            </label>
            <textarea
              value={bugSteps}
              onChange={(e) => setBugSteps(e.target.value)}
              placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              What did you expect?
            </label>
            <input
              type="text"
              value={bugExpected}
              onChange={(e) => setBugExpected(e.target.value)}
              placeholder="What should have happened instead"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              Browser and environment information will be included automatically.
            </p>
          </div>

          <button
            type="submit"
            disabled={submittingBug || !bugTitle.trim() || !bugDescription.trim()}
            className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed transition-colors"
          >
            {submittingBug ? (
              <>
                <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Bug Report'
            )}
          </button>
        </form>
      )}

      {/* Feature Request Form */}
      {activeTab === 'request' && (
        <form onSubmit={submitFeatureRequest} className="space-y-4">
          {featureSuccess && (
            <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
              <Check className="w-5 h-5" />
              Feature request submitted successfully. Thank you for your feedback!
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Feature Title *
            </label>
            <input
              type="text"
              value={featureTitle}
              onChange={(e) => setFeatureTitle(e.target.value)}
              placeholder="Brief title for your idea"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              What would you like to see? *
            </label>
            <textarea
              value={featureDescription}
              onChange={(e) => setFeatureDescription(e.target.value)}
              placeholder="Describe the feature or improvement..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              How would you use this?
            </label>
            <textarea
              value={featureUseCase}
              onChange={(e) => setFeatureUseCase(e.target.value)}
              placeholder="Describe your use case or problem this solves..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="bg-green-50 border border-green-100 rounded-lg p-3">
            <p className="text-sm text-green-700">
              We review all feature requests and prioritize based on user demand.
            </p>
          </div>

          <button
            type="submit"
            disabled={submittingFeature || !featureTitle.trim() || !featureDescription.trim()}
            className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed transition-colors"
          >
            {submittingFeature ? (
              <>
                <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Feature Request'
            )}
          </button>
        </form>
      )}

      {/* Feedback List */}
      {activeTab === 'feedback' && (
        <div className="space-y-6">
          {loadingFeedback ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" />
              <p className="text-gray-500 mt-2">Loading your feedback...</p>
            </div>
          ) : (
            <>
              {/* Bug Reports */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Bug className="w-5 h-5" />
                  Bug Reports ({bugReports.length})
                </h3>
                {bugReports.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4">
                    No bug reports submitted yet.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {bugReports.map((item) => {
                      const style = getStatusStyle(item.status)
                      return (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {item.title.replace(/^\[(Mobile|Web|Bug)\]\s*/i, '')}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Submitted {formatDate(item.created_at)}
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${style.bg} ${style.text}`}>
                            {style.label}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Feature Requests */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  Feature Requests ({featureRequests.length})
                </h3>
                {featureRequests.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4">
                    No feature requests submitted yet.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {featureRequests.map((item) => {
                      const style = getStatusStyle(item.status)
                      return (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {item.title.replace(/^\[(Feature)\]\s*/i, '')}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Submitted {formatDate(item.created_at)}
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${style.bg} ${style.text}`}>
                            {style.label}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
